/**
 * The QuickBooks operator surface's client (QBO API v2, slice 6b).
 *
 * Sibling-module pattern (clientsApi.ts): plain fetch + a local
 * parseOrThrow, rather than reaching into api.ts's private verbs.
 *
 * Everything the server sends here is FACT-SHAPED — outcome tokens,
 * class tokens, ids, dates. This module's job on the way back is to turn
 * those tokens into operator sentences, in ONE place, so a token nobody
 * has written a sentence for still renders as itself rather than as
 * blank space.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import { absenceIsKnown, isPushFailureClass, pushFailureSentence } from './qboPushStatus';
import { contactSentence, type SupportContact } from './errorCopy';

// ---------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------

/** One company as the 'multiple' arm names it (2026-08-31 name train):
 *  the realm id plus the stored company name, null when the config
 *  holds none — the surface then says "company {realm_id}". */
export interface QboConnectionCompanyWire {
  realm_id: string;
  company_name: string | null;
}

/** 'multiple' is a WIRE state, not a store state: the token store's
 *  QboConnectionRead stays a per-connection fact (absent / connected /
 *  expired) and THROWS rather than guess between two companies. The
 *  qbo/status handler is what composes this shape, so the handler is
 *  where the throw becomes the honest token, and the store is untouched.
 *
 *  The 2026-08-31 company-name train: the single arms carry
 *  company_name (from billing.qbo_company_names, captured at connect
 *  time), and 'multiple' carries the companies LIST — it used to carry
 *  no realm at all ("naming one of the two would be the guess the store
 *  refused"), and naming ALL of them is the honest resolution: no
 *  guessing, and the card can finally say which two. Both additions are
 *  optional on the type so an older response still renders (the
 *  clients-roster precedent), falling back to the legacy sentences. */
export type QboConnectionWire =
  | { status: 'absent' }
  | { status: 'multiple'; companies?: QboConnectionCompanyWire[] }
  | {
      status: 'connected' | 'expired';
      realmId: string;
      connectedAt: string;
      refreshTokenExpiresAt: string;
      company_name?: string | null;
    };

export interface QboCustomerWire {
  qbo_customer_id: string;
  display_name: string;
  active: boolean;
}

export type QboLinkProposalWire =
  | { kind: 'already_linked'; client_id: string; qbo_customer_id: string }
  | { kind: 'linked_missing'; client_id: string; qbo_customer_id: string }
  | { kind: 'exact'; client_id: string; qbo_customer_id: string; display_name: string }
  | { kind: 'ambiguous'; client_id: string; candidates: QboCustomerWire[] }
  | {
      kind: 'contested';
      client_id: string;
      candidates: QboCustomerWire[];
      contested_by: string[];
    }
  | { kind: 'none'; client_id: string; candidates: QboCustomerWire[] };

export type QboLinkProposalKind = QboLinkProposalWire['kind'];

export type QboLinkProposalsWire =
  | {
      outcome: 'proposed';
      realm_id: string;
      proposals: QboLinkProposalWire[];
      counts: Record<QboLinkProposalKind, number>;
      /** The client roster the proposals were matched from, id -> name,
       *  so the ceremony can show WHO without a second round trip and
       *  without a name ever entering a proposal object (proposals can
       *  reach an error; a roster read cannot). Optional on the wire so
       *  an older response still renders, falling back to the id. */
      clients?: Array<{ id: string; display_name: string }>;
    }
  | { outcome: 'unavailable'; reason: string };

export type QboLinkConfirmWire =
  | {
      outcome: 'linked';
      client_id: string;
      qbo_customer_id: string;
      realm_id: string;
      customer_active: boolean;
    }
  | { outcome: 'refused'; reason: string; client_id: string; qbo_customer_id: string }
  | { outcome: 'unavailable'; reason: string; client_id: string };

export interface QboPushResultWire {
  // 'pushed_total_unverifiable' (sweep F6): in QuickBooks with its id,
  // but the totals oracle could not COMPUTE its comparison — kept
  // distinct from 'pushed_total_mismatch', whose false flag means the
  // totals were compared and disagree.
  outcome:
    | 'pushed'
    | 'pushed_total_mismatch'
    | 'pushed_total_unverifiable'
    | 'already_pushed'
    | 'failed'
    | 'refused';
  reason?: string;
  attemptId: string | null;
  qboInvoiceId?: string;
  /** true = compared and equal; false = compared and different; ABSENT
   *  on the unverifiable arm (no comparison happened). */
  totalMatchesSnapshot?: boolean;
}

// ---------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function base(slug: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1`;
}


/** One row of the item map screen. `qbo_item_id` is null when the band
 *  has no mapping; `on_unpushed_invoice` is the urgent half - that token
 *  is on an issued invoice waiting to go, and the push fails closed
 *  without it. */
export interface QboItemMapRowWire {
  band: string;
  label: string;
  qbo_item_id: string | null;
  on_unpushed_invoice: boolean;
}

export interface QboItemMapWire {
  bands: QboItemMapRowWire[];
  bound_realm_id: string | null;
  connection: {
    status: 'connected' | 'expired' | 'absent' | 'multiple';
    realm_id: string | null;
  };
  request_id: string;
}

/** The item-map write answers, mirroring the SERVER's closed set: the
 *  handler (api/src/functions/qboItemMap.ts) emits exactly 'mapped',
 *  'refused', 'unavailable' (PUT) and 'cleared' (DELETE), all as 200s.
 *  A discriminated union, not `outcome: string`: the plain-string
 *  version let the screen treat "not refused" as "saved", and an
 *  'unavailable' (nothing verified, nothing written) rendered exactly
 *  like success. The confirm ceremony's QboLinkConfirmWire is the
 *  precedent.
 *
 *  'refused' is the server saying NO to this id ('item_not_in_realm',
 *  'multiple_connections', ...); 'unavailable' is the server saying it
 *  could not find out (a token_* class from resolveUsableAccess, or
 *  'item_read_failed' when the product list would not read). Both mean
 *  the map is unchanged; only the sentence differs. */
export type QboItemMapWriteWire =
  | {
      outcome: 'mapped';
      band: string;
      qbo_item_id: string;
      realm_id: string;
      item_name: string;
      item_active: boolean;
      request_id: string;
    }
  | {
      outcome: 'refused' | 'unavailable';
      reason: string;
      band: string;
      qbo_item_id: string;
      request_id: string;
    };

/** The DELETE's answers, split from the PUT's (D4-9): only 'cleared' is
 *  a reset that happened. 'unavailable' ('map_clear_failed') is the
 *  server saying the write failed and NOTHING changed — a 200, because
 *  a settings screen never 500s, which is exactly why the caller must
 *  read the outcome and not the status. */
export type QboItemMapClearWire =
  | { outcome: 'cleared'; request_id: string }
  | { outcome: 'unavailable'; reason: string; request_id: string };

export const qboApi = {
  status(slug: string, signal?: AbortSignal): Promise<{ connection: QboConnectionWire }> {
    const path = `${base(slug)}/qbo/status`;
    return fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal }).then(
      (res) => parseOrThrow<{ connection: QboConnectionWire }>(res, `GET ${path}`),
    );
  },

  connectStart(slug: string, signal?: AbortSignal): Promise<{ authorize_url: string }> {
    const path = `${base(slug)}/qbo/connect/start`;
    return writeFetch(path, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal,
    }).then((res) => parseOrThrow<{ authorize_url: string }>(res, `POST ${path}`));
  },

  disconnect(slug: string, realmId: string, signal?: AbortSignal): Promise<unknown> {
    const path = `${base(slug)}/qbo/disconnect`;
    return writeFetch(path, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ realm_id: realmId }),
      signal,
    }).then((res) => parseOrThrow<unknown>(res, `POST ${path}`));
  },

  itemMap(slug: string, signal?: AbortSignal): Promise<QboItemMapWire> {
    const path = `${base(slug)}/qbo/item-map`;
    return fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal }).then(
      (res) => parseOrThrow<QboItemMapWire>(res, `GET ${path}`),
    );
  },

  mapItem(
    slug: string,
    body: { band: string; qbo_item_id: string },
    signal?: AbortSignal,
  ): Promise<QboItemMapWriteWire> {
    const path = `${base(slug)}/qbo/item-map`;
    return writeFetch(path, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }).then((res) => parseOrThrow<QboItemMapWriteWire>(res, `PUT ${path}`));
  },

  /** Clears every mapping AND the company binding. The ids in the map
   *  belong to the company it was bound to, so they are meaningless in a
   *  new one - which is why there is no way to keep them. */
  clearItemMap(slug: string, signal?: AbortSignal): Promise<QboItemMapClearWire> {
    const path = `${base(slug)}/qbo/item-map`;
    return writeFetch(path, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      signal,
    }).then((res) => parseOrThrow<QboItemMapClearWire>(res, `DELETE ${path}`));
  },

  linkProposals(slug: string, signal?: AbortSignal): Promise<QboLinkProposalsWire> {
    const path = `${base(slug)}/qbo/customer-links`;
    return fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal }).then(
      (res) => parseOrThrow<QboLinkProposalsWire>(res, `GET ${path}`),
    );
  },

  confirmLink(
    slug: string,
    body: { client_id: string; qbo_customer_id: string; replace?: boolean },
    signal?: AbortSignal,
  ): Promise<QboLinkConfirmWire> {
    const path = `${base(slug)}/qbo/customer-links`;
    return writeFetch(path, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }).then((res) => parseOrThrow<QboLinkConfirmWire>(res, `POST ${path}`));
  },

  /** Undo a customer link. The push then fails CLOSED on that client
   *  until it is relinked, which is the correct posture: better a
   *  refused push than an invoice in a stranger's ledger. */
  unlink(
    slug: string,
    clientId: string,
    signal?: AbortSignal,
  ): Promise<{ unlinked: boolean; client_id: string }> {
    const path = `${base(slug)}/qbo/customer-links/${encodeURIComponent(clientId)}`;
    return writeFetch(path, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      signal,
    }).then((res) => parseOrThrow<{ unlinked: boolean; client_id: string }>(res, `DELETE ${path}`));
  },

  push(slug: string, invoiceId: string, signal?: AbortSignal): Promise<QboPushResultWire> {
    const path = `${base(slug)}/invoices/${encodeURIComponent(invoiceId)}/qbo-push`;
    return writeFetch(path, { method: 'POST', headers: { Accept: 'application/json' }, signal }).then(
      (res) => parseOrThrow<QboPushResultWire>(res, `POST ${path}`),
    );
  },

  /**
   * A human confirming a vanished QuickBooks copy was removed on purpose.
   *
   * This writes nothing to QuickBooks. Olive's push is create-only and it
   * never acts across the seam; all this records is that somebody looked
   * over there and agrees. It exists so the one copy state that cannot
   * resolve itself has an off switch.
   *
   * Answers 404 for anything that is not this tenant's unconfirmed
   * missing copy, including a copy Olive has since seen open. That is the
   * screen having moved on, not a failure to try.
   */
  acknowledgeCopyRemoval(
    slug: string,
    invoiceId: string,
    signal?: AbortSignal,
  ): Promise<{ acknowledged: boolean }> {
    const path = `${base(slug)}/invoices/${encodeURIComponent(invoiceId)}/qbo-copy-removal-ack`;
    return writeFetch(path, { method: 'POST', headers: { Accept: 'application/json' }, signal }).then(
      (res) => parseOrThrow<{ acknowledged: boolean }>(res, `POST ${path}`),
    );
  },
};

// ---------------------------------------------------------------------
// The batch (founder ruling 2026-08-25)
// ---------------------------------------------------------------------

export interface BatchPushTarget {
  id: string;
  doc_number: string | null;
  client_display_name: string;
}

export interface BatchPushOutcome {
  target: BatchPushTarget;
  result: QboPushResultWire;
}

export interface BatchPushReport {
  /** Pushed by THIS run: the send the operator just watched happen. */
  done: BatchPushOutcome[];
  /** QuickBooks already held it, so this run sent nothing for it. Its
   *  own bucket (contract item 1, F2): counted with `done`, re-running a
   *  finished month reported sends that never happened. */
  already: BatchPushOutcome[];
  /** In QuickBooks but the total is not certified: it did not match the
   *  frozen invoice, or (sweep F6) it could not be read to compare at
   *  all. NOT a failure to retry — retrying would not fix it, and it is
   *  already in the books. It needs eyes. */
  attention: BatchPushOutcome[];
  /** Olive KNOWS nothing reached QuickBooks: refused before the wire,
   *  the connection never opened, or Intuit answered and declined. The
   *  only bucket allowed to say "did not send". */
  failed: BatchPushOutcome[];
  /** The request left and the answer did not come back. Olive does not
   *  know whether it arrived, and the report may not claim it missed. */
  unconfirmed: BatchPushOutcome[];
}

/**
 * Push each selected invoice, ONE AT A TIME, through the same
 * per-invoice route a single push uses.
 *
 * This sequencing is the whole design, not an implementation detail.
 * Each invoice keeps its own attempt row and its own stable requestid
 * derived from its own content, so a batch is exactly N independent
 * pushes that happen to have been started by one click. There is no bulk
 * endpoint to write and no shared transaction to half-commit: twenty
 * invoices with three problems is seventeen pushed and three named
 * failures, every time.
 *
 * It also never throws. A batch that abandoned the remaining invoices
 * because the fourth one hit a dead socket would be worse than useless
 * at month end, when the operator is trying to finish.
 */
export async function pushInvoicesSequentially(
  slug: string,
  targets: readonly BatchPushTarget[],
  opts: {
    onProgress?: (completed: number, total: number) => void;
    push?: (slug: string, invoiceId: string) => Promise<QboPushResultWire>;
  } = {},
): Promise<BatchPushReport> {
  const push = opts.push ?? ((s, id) => qboApi.push(s, id));
  const report: BatchPushReport = { done: [], already: [], attention: [], failed: [], unconfirmed: [] };

  for (const [index, target] of targets.entries()) {
    let result: QboPushResultWire;
    try {
      result = await push(slug, target.id);
    } catch (err) {
      // A transport failure is a fact about THIS invoice, not about the
      // batch. Class token only: nothing the network said gets rendered.
      //
      // Sorted by what Olive can PROVE (QBO sweep D4-4, 2026-08-31).
      // The push route answers its results as 200 facts, so a 4xx from
      // Olive's OWN API means the preamble or a validator said no
      // BEFORE the push service ran: provable non-delivery, a pre-wire
      // refusal like any other, and it belongs in `failed` — not in
      // `unconfirmed`, which sends the operator to QuickBooks hunting a
      // copy that cannot exist. A 5xx or a dead socket keeps the
      // delivery-unknown class: Olive's host may have finished the
      // create before it died.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        result = { outcome: 'refused', reason: 'push_request_refused', attemptId: null };
      } else {
        result = { outcome: 'failed', reason: 'push_request_failed', attemptId: null };
      }
    }
    if (result.outcome === 'pushed') {
      report.done.push({ target, result });
    } else if (result.outcome === 'already_pushed') {
      report.already.push({ target, result });
    } else if (
      result.outcome === 'pushed_total_mismatch' ||
      result.outcome === 'pushed_total_unverifiable'
    ) {
      // Both are IN QuickBooks with a total nobody has certified (one
      // compared-and-wrong, one never compared). Neither may read as a
      // failure to retry — a resend would duplicate — and neither may
      // read as unconfirmed delivery: the id is on the row.
      report.attention.push({ target, result });
    } else if (result.outcome === 'refused' || absenceIsKnown(result.reason)) {
      // A refusal is a pre-wire no by construction, whatever its class;
      // a failure may only claim non-delivery when its class is ruled
      // knowable. Everything else is unconfirmed, never "did not send".
      report.failed.push({ target, result });
    } else {
      report.unconfirmed.push({ target, result });
    }
    opts.onProgress?.(index + 1, targets.length);
  }

  return report;
}

// ---------------------------------------------------------------------
// Tokens into sentences
// ---------------------------------------------------------------------

/** The link ceremony's and transport's own tokens, in operator words.
 *
 *  The PUSH failure classes are deliberately NOT here (contract item 1):
 *  their sentences live in qboPushStatus.ts, in the one map every push
 *  surface renders from, and qboReasonSentence delegates to it. A second
 *  sentence here for a push class is the drift mechanism itself: two
 *  files, one fact, and the next edit changes one of them. */
export const REASON_SENTENCES: Readonly<Record<string, string>> = {
  // Push refusals (the server said no before anything went anywhere)
  invoice_not_found: "That invoice can't be found, so nothing was sent. Reload the page and pick it again.",
  invoice_voided: "That invoice is voided, so it wasn't sent. Issue a new one if it still needs to go to QuickBooks.",
  invoice_not_issued: 'Only issued invoices go to QuickBooks, so nothing was sent. Issue this one first, then send it.',
  already_pushed: "This invoice is already in QuickBooks. There's nothing more to send from here.",
  push_in_flight: 'This invoice is already on its way to QuickBooks, so nothing more was sent. Wait a minute, then reload the page to see whether it arrived.',
  // Minted by pushInvoicesSequentially's catch (D4-4): Olive's own API
  // answered 4xx, so the push service never ran and nothing left. A
  // transport-tier fact this module owns, so its sentence lives here,
  // not in qboPushStatus's push-failure registry.
  push_request_refused:
    "This didn't send, and nothing reached QuickBooks. Check the invoice and send again, and if it fails again, tell {contact_billing}.",
  rate_band: 'A line uses a rate band that has no QuickBooks product yet, so nothing was sent. Pick its product in Admin, Billing, QuickBooks, then send again.',
  service_item_id:
    'A QuickBooks product is missing from the settings, so nothing was sent. Ask {contact_billing} to set it in Admin, Billing, QuickBooks, then send again.',
  // Connection and transport. The five token_* classes are NOT here: they
  // are push failures and qboPushStatus rules them, sentence and all. A
  // key in both maps renders the unruled copy, because the lookup below
  // reads this map first.
  reconnect_required: 'The QuickBooks connection needs renewing, so nothing was sent. Press Reconnect QuickBooks in Admin, Billing, QuickBooks, then send again.',
  customer_read_failed: "Your QuickBooks customers couldn't be read. Press Match clients to QuickBooks customers again in a minute, and if it fails again, reconnect QuickBooks.",
  client_read_failed: "Your client list couldn't be read. Reload the page, then press Match clients to QuickBooks customers again.",
  // The void gate's unavailableReason token (0172): the invoice was
  // pushed to a DIFFERENT QuickBooks company than the one connected now,
  // so Olive refused to read the copy (the same invoice number here
  // belongs to someone else). One honest sentence; the act is in the
  // other company, not on this screen.
  realm_changed:
    'This invoice was sent to a different QuickBooks company than the one connected now. Check it in the company it was sent to.',
  // Link refusals
  customer_not_in_realm: "That customer isn't in the connected QuickBooks company. Pick one from the list, or reconnect to the right company.",
  client_already_linked:
    'This client is already linked to a different customer. Unlink it first if the link is wrong.',
  qbo_customer_already_linked:
    "That QuickBooks customer is already linked to another client. If the two share one invoice, use Combine with another client on this client's Billing details.",
  client_not_found: "That client can't be found. Reload the page and pick the client again.",
  // client_not_linked_error was removed here (QBO sweep, 2026-08-31): no
  // server tier ever emits that token. The REAL class is client_not_linked
  // (classifyQboPushFailure names the error class into it), a push
  // failure whose one ruled sentence lives in qboPushStatus.ts — an
  // entry here would have SHADOWED it, because this map is read first.
  // The dead key looked like coverage and was the drift mechanism.
  link_write_failed: "The link didn't save. Try again, and if it happens again, tell {contact_system}.",
  invalid_qbo_customer_id: 'Pick a QuickBooks customer to link.',
  invalid_client_id: 'Pick a client to link.',
};

/** A sentence that names who to tell carries `{contact_billing}` or
 *  `{contact_system}`, filled here from the caller's support contact. */
export function qboReasonSentence(reason: string | undefined, contact: SupportContact): string {
  if (reason !== undefined && reason !== '') {
    const own = REASON_SENTENCES[reason];
    if (own !== undefined) return contactSentence(own, contact);
    // Every push class gets the ONE sentence the copy module rules for
    // it, so the batch bar, the panel, and the list cannot drift.
    if (isPushFailureClass(reason)) return pushFailureSentence(reason, contact);
    // A token that is neither ours nor the push module's: say something
    // true without rendering the wire token on a money screen.
    return contactSentence(
      "QuickBooks turned this invoice down, so nothing was sent. Check its customer and lines and send again, and if it's turned down again, tell {contact_billing}.",
      contact,
    );
  }
  // No class at all is the purest case of not knowing: it gets the
  // unclassified sentence, never a refusal's.
  return pushFailureSentence(reason ?? null, contact);
}

/**
 * The company as a person knows it: the stored name, or, when the config
 * holds none, a fallback that still SAYS what the number is — "company
 * {realm_id}", never a bare 19-digit mystery. Shared, so the card and the
 * rail's summary cannot name the same company two different ways.
 */
export function companyLabel(name: string | null | undefined, realmId: string): string {
  return name != null && name.trim() !== '' ? name : `company ${realmId}`;
}
